import { useState } from 'react';
import { Formik, Form } from 'formik';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import FloatingLabelInput from '@/components/FloatingLabelInput';
import { useAuth } from '@/hooks/auth.hook';
import { getValidationSchema, getInitialValues } from '@/utils/validation';
import { MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FieldType = {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
};

export default function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { changePassword } = useAuth();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (values: FieldType, { setSubmitting, resetForm }: any) => {
    try {
      const result = await changePassword(
        values.oldPassword,
        values.newPassword,
        values.newPasswordConfirmation,
      );
      if (result?.succeeded) {
        resetForm();
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Change password error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] border dark:border-[#3A3A3A] p-[2.5rem] dark:bg-[#202020] bg-[#F6F6F6]">
        <DialogHeader>
          <DialogTitle className="text-[2.4rem] font-bold">Change Password</DialogTitle>
          <DialogDescription className="text-[1.6rem]">
            Enter your current password and new password to update your account.
          </DialogDescription>
        </DialogHeader>

        <Formik
          initialValues={getInitialValues('changePassword') as FieldType}
          validationSchema={getValidationSchema('changePassword')}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-6 mt-6">
              <div>
                <FloatingLabelInput
                  name="oldPassword"
                  label="Current Password"
                  type={showOldPassword ? 'text' : 'password'}
                  placeholder="Current Password"
                  rightIcon={
                    showOldPassword ? (
                      <MdOutlineVisibility className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    ) : (
                      <MdOutlineVisibilityOff className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    )
                  }
                  onRightIconClick={() => setShowOldPassword(!showOldPassword)}
                />
              </div>

              <div>
                <FloatingLabelInput
                  name="newPassword"
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="New Password"
                  rightIcon={
                    showNewPassword ? (
                      <MdOutlineVisibility className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    ) : (
                      <MdOutlineVisibilityOff className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    )
                  }
                  onRightIconClick={() => setShowNewPassword(!showNewPassword)}
                />
              </div>

              <div>
                <FloatingLabelInput
                  name="newPasswordConfirmation"
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm New Password"
                  rightIcon={
                    showConfirmPassword ? (
                      <MdOutlineVisibility className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    ) : (
                      <MdOutlineVisibilityOff className="w-[2.4rem] h-[2.4rem] text-[#A2A2A2]" />
                    )
                  }
                  onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="text-[1.6rem] px-8 filterBtnStyle black-custom"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="text-[1.6rem]  filterBtnStyle px-8 bg-[#57E8BA] hover:bg-[#4DD4A8] text-black"
                >
                  {isSubmitting ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

